<!-- components/Accordion.vue -->
<script setup lang="ts">
type Props = {
  title: string;
  id: string;
  pillText?: string;
  pillColor?: 'red' | 'green' | 'yellow' | 'gray';
  showIcon?: boolean;
};

const {
  title,
  id,
  pillText,
  pillColor,
  showIcon = false,
} = defineProps<Props>();

const isOpen = ref(false);

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const { accordionToOpen } = useAccountProtection();

watch(
  accordionToOpen,
  (newValue) => {
    if (!newValue) {
      return;
    }

    if (newValue === id) {
      isOpen.value = true;
    } else {
      isOpen.value = false;
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div
    :id="id"
    :class="$style.accordionItem"
  >
    <label
      :class="[$style.accordionLabel, { [$style.active]: isOpen }]"
      @click="toggle"
    >
      <div :class="$style.accordionTitle">
        <span>{{ title }}</span>
        <span
          v-if="pillText && pillColor"
          :class="[$style.pill, $style[pillColor]]"
        >
          {{ pillText }}
        </span>
        <NuxtIcon
          v-if="showIcon"
          :class="$style.iconTimer"
          name="timer"
        />
      </div>
      <NuxtIcon
        :class="$style.icon"
        name="arrows/angle_down"
      />
    </label>
    <div
      v-if="isOpen"
      :class="$style.accordionContent"
    >
      <slot></slot>
    </div>
  </div>
</template>

<style module lang="postcss">
.accordionItem {
  @apply relative flex flex-col border-b border-texts/10 border-solid;

  .accordionLabel {
    @apply cursor-pointer flex justify-between items-center text-xs md:text-sm font-semibold py-5 px-2 select-none;

    &:hover {
      @apply opacity-90;
    }

    .accordionTitle {
      @apply flex items-center gap-2;

      .pill {
        @apply px-1.5 py-0.5 rounded-full text-2xs leading-3;

        &.red {
          @apply bg-error text-white;
        }

        &.green {
          @apply bg-success text-white;
        }

        &.yellow {
          @apply bg-warning text-white;
        }

        &.gray {
          @apply bg-texts/10 text-white/40;
        }
      }

      .iconTimer {
        @apply text-warning;
      }
    }

    .icon {
      @apply ml-2;
    }

    &.active {
      .icon {
        @apply rotate-180;
      }
    }
  }

  .accordionContent {
    @apply px-2 pb-6;
  }
}
</style>
